/**
 * Cliente das rotas de modelo de treino.
 *
 * **A gravação envia o modelo inteiro, itens incluídos.** É o contrato do
 * servidor, e o motivo está registrado lá: com envio parcial, "removi o último
 * item" fica indistinguível de "não mexi nos itens".
 *
 * Consequência para a tela: ela guarda o modelo em memória enquanto a pessoa
 * edita, e grava de uma vez. Não há salvamento por campo, e é por isso que a
 * versão importa — ver `useTemplates.ts`.
 */

import { api } from '@/lib/apiClient';

export type TemplateStatus = 'active' | 'archived';

/**
 * O tipo da prescrição, que **não** é a modalidade do modelo.
 *
 * `running_walking` é modalidade; a prescrição correspondente chama-se
 * `cardio`. A diferença é real: um treino de musculação pode terminar com dez
 * minutos de esteira, e o item carrega o próprio formato.
 */
export type PrescriptionType = 'strength' | 'cardio' | 'functional' | 'mobility' | 'swimming';

export type IntensityLabel = 'easy' | 'comfortable' | 'moderate' | 'hard' | 'very_hard';

export type Stroke =
  'freestyle' | 'backstroke' | 'breaststroke' | 'butterfly' | 'medley' | 'kick' | 'drill' | 'mixed';

export interface StrengthPrescription {
  type: 'strength';
  sets: number;
  reps_min: number;
  reps_max: number;
  target_load_kg?: number | null;
  rest_seconds?: number | null;
  target_rpe?: number | null;
}

export interface CardioInterval {
  repetitions: number;
  work_seconds: number;
  rest_seconds?: number;
  intensity_label?: IntensityLabel | null;
}

export interface CardioPrescription {
  type: 'cardio';
  target_duration_seconds?: number | null;
  target_distance_m?: number | null;
  intensity_label?: IntensityLabel | null;
  target_rpe_min?: number | null;
  target_rpe_max?: number | null;
  intervals?: CardioInterval[];
}

export interface FunctionalPrescription {
  type: 'functional';
  rounds: number;
  work_seconds?: number | null;
  rest_seconds?: number | null;
  target_reps?: number | null;
  format?: 'interval' | 'amrap' | 'for_time' | 'circuit';
}

export interface MobilityPrescription {
  type: 'mobility';
  duration_seconds?: number | null;
  repetitions?: number | null;
  side?: 'both' | 'left' | 'right' | 'alternating';
  hold_seconds?: number | null;
}

export interface SwimSet {
  repetitions: number;
  distance_m: number;
  stroke?: Stroke;
  rest_seconds?: number | null;
  intensity_label?: IntensityLabel | null;
}

export interface SwimmingPrescription {
  type: 'swimming';
  pool_length: 25 | 50;
  pool_unit: 'm' | 'yd';
  target_distance_m?: number | null;
  target_duration_seconds?: number | null;
  target_rpe?: number | null;
  sets?: SwimSet[];
}

export type Prescription =
  | StrengthPrescription
  | CardioPrescription
  | FunctionalPrescription
  | MobilityPrescription
  | SwimmingPrescription;

export interface TemplateItem {
  item_id: string;
  position: number;
  block_label: string | null;
  exercise_id: string | null;
  custom_title: string | null;
  instructions: string | null;
  prescription: Prescription;
}

export interface Template {
  template_id: string;
  title: string;
  content_locale: string;
  modality: string;
  objective: string | null;
  level: string;
  estimated_minutes: number | null;
  status: TemplateStatus;
  /** Volta em toda resposta: sem ela a tela teria de recarregar para gravar de novo. */
  version: number;
  items: TemplateItem[];
}

export interface TemplatePage {
  items: Template[];
  total: number;
}

export interface TemplateDraft {
  title: string;
  content_locale?: string;
  modality: string;
  objective: string | null;
  level: string;
  estimated_minutes: number | null;
  items: Array<Omit<TemplateItem, 'item_id' | 'position'>>;
}

export const listTemplates = (status: TemplateStatus = 'active') =>
  api.get<TemplatePage>(`/templates?status=${status}&limit=100`);

export const getTemplate = (templateId: string) => api.get<Template>(`/templates/${templateId}`);

export const createTemplate = (draft: TemplateDraft) => api.post<Template>('/templates', draft);

/** `version` é a que a tela tinha em mãos. Atrasada, o servidor devolve 409. */
export const saveTemplate = (templateId: string, draft: TemplateDraft, version: number) =>
  api.put<Template>(`/templates/${templateId}`, { ...draft, version });

export const setTemplateStatus = (templateId: string, status: TemplateStatus, version: number) =>
  api.put<Template>(`/templates/${templateId}/status`, { status, version });

export const duplicateTemplate = (templateId: string, title: string) =>
  api.post<Template>(`/templates/${templateId}/duplicate`, { title });
